// Accessibility utilities

/**
 * Check if user prefers reduced motion
 */
export const prefersReducedMotion = (): boolean => {
  if (typeof window === "undefined") return false;
  
  if (window.matchMedia) {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }
  
  return false;
};

/**
 * Get reduced motion-aware animation variants for Framer Motion
 */
export const getMotionVariants = (defaultVariants: any, reducedVariants?: any) => {
  if (prefersReducedMotion()) {
    return reducedVariants || {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 }
    };
  }
  
  return defaultVariants;
};

/**
 * Check color contrast ratio between two colors
 * Returns ratio (should be >= 4.5 for normal text, >= 3 for large text)
 */
export const getContrastRatio = (color1: string, color2: string): number => {
  const getLuminance = (color: string): number => {
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;

    // Apply gamma correction
    const rgb = [r, g, b].map(c => {
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
  };

  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
};

/**
 * Validate color contrast for accessibility
 */
export const validateContrast = (foreground: string, background: string, isLargeText = false): boolean => {
  const ratio = getContrastRatio(foreground, background);
  return ratio >= (isLargeText ? 3 : 4.5);
};

/**
 * Announce text to screen readers via ARIA live region
 */
export const announceToScreenReader = (message: string) => {
  const region = document.getElementById('live-region');
  if (region) {
    region.textContent = message;
    // Clear after announcement to allow re-announcement of same message
    setTimeout(() => {
      if (region.textContent === message) {
        region.textContent = '';
      }
    }, 1000);
  }
};
